import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tag, Plus, Trash2, Loader2, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SCOPE_LABELS = {
  all: { label: 'Todos los Tracks', color: 'text-green-400 bg-green-500/10' },
  genre: { label: 'Por Genero', color: 'text-blue-400 bg-blue-500/10' },
  specific: { label: 'Tracks Especificos', color: 'text-purple-400 bg-purple-500/10' },
};

const PromotionsPanel = () => {
  const [promos, setPromos] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [genres, setGenres] = useState([]);
  // Form
  const [title, setTitle] = useState('');
  const [discountPct, setDiscountPct] = useState(50);
  const [scope, setScope] = useState('all');
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [durationDays, setDurationDays] = useState(7);
  const [bannerText, setBannerText] = useState('');

  const loadPromos = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/promotions`, { withCredentials: true });
      setPromos(r.data);
    } catch {}
  }, []);

  const loadGenres = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/tracks/genres/list`);
      setGenres(r.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadPromos(); loadGenres(); }, []);

  const createPromo = async () => {
    if (!title || discountPct <= 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/promotions`, {
        title,
        discount_pct: discountPct,
        scope,
        genres: scope === 'genre' ? selectedGenres : [],
        track_ids: [],
        duration_days: durationDays,
        banner_text: bannerText || `${discountPct}% OFF - ${title}`,
        is_active: true,
      }, { withCredentials: true });
      setShowCreate(false);
      setTitle(''); setDiscountPct(50); setScope('all'); setSelectedGenres([]); setBannerText('');
      loadPromos();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    setLoading(false);
  };

  const togglePromo = async (id) => {
    try {
      await axios.put(`${API}/api/admin/promotions/${id}/toggle`, {}, { withCredentials: true });
      loadPromos();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const deletePromo = async (id) => {
    if (!window.confirm('Eliminar esta promocion?')) return;
    try {
      await axios.delete(`${API}/api/admin/promotions/${id}`, { withCredentials: true });
      loadPromos();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const getTimeLeft = (expiresAt) => {
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Expirada';
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    if (days > 0) return `${days}d ${hours}h restantes`;
    return `${hours}h restantes`;
  };

  return (
    <div data-testid="promotions-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">Ofertas y Promociones</h2>
          <p className="text-xs text-white/40 mt-1">Crea descuentos con auto-apagado. Los clientes ven la oferta al entrar.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={loadPromos} className="p-2 hover:bg-white/10 rounded-lg text-white/40"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setShowCreate(!showCreate)}
            className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl text-xs font-bold flex items-center gap-1.5"
            data-testid="create-promo-btn">
            <Plus className="w-4 h-4" /> Nueva Oferta
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="glass-card p-5 mb-6 border border-[#E8720C]/20 space-y-4" data-testid="create-promo-form">
          <h3 className="text-sm font-semibold">Crear Promocion</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-white/40 block mb-1">Titulo de la Oferta</label>
              <input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Ej: Black Friday, Semana del Reggaeton..."
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#E8720C]/50" />
            </div>
            <div>
              <label className="text-[10px] text-white/40 block mb-1">% de Descuento</label>
              <div className="flex gap-2">
                {[10, 20, 30, 40, 50, 60, 70].map(pct => (
                  <button key={pct} onClick={() => setDiscountPct(pct)}
                    className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                      discountPct === pct ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                    }`}>{pct}%</button>
                ))}
              </div>
            </div>
          </div>

          {/* Scope */}
          <div>
            <label className="text-[10px] text-white/40 block mb-2">Aplica a:</label>
            <div className="flex gap-2">
              {Object.entries(SCOPE_LABELS).map(([key, val]) => (
                <button key={key} onClick={() => setScope(key)}
                  className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                    scope === key ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}>{val.label}</button>
              ))}
            </div>
          </div>

          {/* Genre selector */}
          {scope === 'genre' && (
            <div>
              <label className="text-[10px] text-white/40 block mb-2">Selecciona Generos:</label>
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 bg-black/20 rounded-xl">
                {genres.map(g => (
                  <button key={g} onClick={() => {
                    setSelectedGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
                  }}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                      selectedGenres.includes(g) ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                    }`}>{g}</button>
                ))}
              </div>
              {selectedGenres.length > 0 && (
                <p className="text-xs text-[#E8720C] mt-1">{selectedGenres.length} genero(s) seleccionados</p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] text-white/40 block mb-1">Duracion (dias) - se apaga automatico</label>
              <div className="flex gap-2">
                {[1, 3, 5, 7, 14, 30].map(d => (
                  <button key={d} onClick={() => setDurationDays(d)}
                    className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                      durationDays === d ? 'bg-[#E8720C] text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'
                    }`}>{d}d</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-white/40 block mb-1">Texto del Banner (lo que ve el cliente)</label>
              <input value={bannerText} onChange={e => setBannerText(e.target.value)}
                placeholder={`${discountPct}% OFF - ${title || 'Tu Oferta'}`}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#E8720C]/50" />
            </div>
          </div>

          {/* Preview */}
          <div className="p-4 bg-gradient-to-r from-[#E8720C]/20 to-[#E8720C]/5 border border-[#E8720C]/30 rounded-xl">
            <p className="text-[10px] text-white/40 mb-2">Vista Previa del Banner:</p>
            <div className="text-center py-3">
              <p className="text-lg font-bold text-[#E8720C]">{bannerText || `${discountPct}% OFF - ${title || 'OFERTA ESPECIAL'}`}</p>
              <p className="text-[10px] text-white/40 mt-1">Se apaga automaticamente en {durationDays} dias</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={createPromo} disabled={loading || !title}
              className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] text-white rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="save-promo-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
              Activar Oferta
            </button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-white/40 text-xs">Cancelar</button>
          </div>
        </div>
      )}

      {/* Promotions List */}
      {promos.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Tag className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">No hay promociones creadas</p>
          <p className="text-[10px] text-white/20 mt-1">Crea una oferta y tus clientes la veran al entrar a la pagina</p>
        </div>
      ) : (
        <div className="space-y-3">
          {promos.map(p => {
            const scopeInfo = SCOPE_LABELS[p.scope] || SCOPE_LABELS.all;
            return (
              <div key={p.id} className={`glass-card p-4 border-l-4 ${p.is_active ? 'border-green-500' : p.is_expired ? 'border-red-500/30' : 'border-white/10'}`}
                data-testid={`promo-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${p.is_active ? 'bg-green-500/15' : 'bg-white/5'}`}>
                      <span className={`text-lg font-black ${p.is_active ? 'text-green-400' : 'text-white/20'}`}>{p.discount_pct}%</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-white">{p.title}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${scopeInfo.color}`}>{scopeInfo.label}</span>
                        {p.scope === 'genre' && p.genres?.length > 0 && (
                          <span className="text-[9px] text-white/30">{p.genres.join(', ')}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-[10px] text-white/30">"{p.banner_text}"</span>
                        {p.is_active && (
                          <span className="flex items-center gap-1 text-[10px] text-green-400">
                            <Clock className="w-3 h-3" /> {getTimeLeft(p.expires_at)}
                          </span>
                        )}
                        {p.is_expired && <span className="text-[10px] text-red-400">Expirada</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Toggle switch */}
                    <button onClick={() => togglePromo(p.id)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${p.is_active ? 'bg-green-500' : 'bg-white/10'}`}
                      data-testid={`toggle-promo-${p.id}`}>
                      <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ${p.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                    <button onClick={() => deletePromo(p.id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg text-white/20 hover:text-red-400 transition-colors"
                      data-testid={`delete-promo-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromotionsPanel;
